import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import {Tenant} from 'src/apis/autoGenerate/types/tenant';
import {FrontDeskCallData} from 'src/apis/intercomOpsClient';

export interface FrontDeskCallTarget {
  tenant: Tenant;
  callData: FrontDeskCallData;
  timeoutSeconds?: number;
}

export interface FrontDeskDoorTarget {
  id: string;
  name: string;
  device: IntercomDevice;
}

export function getResidentName(tenant: Tenant) {
  return `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim() || 'Resident';
}

export function getResidentInitials(tenant: Tenant) {
  const first = (tenant.firstName || '').trim().charAt(0);
  const last = (tenant.lastName || '').trim().charAt(0);
  return `${first}${last}`.toUpperCase() || 'R';
}

export function isResidentOnline(tenant: Tenant) {
  const status = String(tenant.status || tenant.user?.status || '').toLowerCase();
  return status === 'created' || status === 'enabled' || status === 'active';
}

export function isDeviceOnline(device: IntercomDevice) {
  const status = String(device.status || '').toUpperCase();
  return status !== 'DEVICE_STATUS_OFFLINE' && status !== 'OFFLINE';
}

export function getDeviceLocation(device: IntercomDevice) {
  const building = device.building?.name;
  const property = device.apartment?.name;
  if (building && property) return `${building} • ${property}`;
  return building || property || '—';
}

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function averageLuminance(context: CanvasRenderingContext2D, width: number, height: number) {
  const sampleWidth = Math.min(width, 80);
  const sampleHeight = Math.min(height, 80);
  const {data} = context.getImageData(0, 0, sampleWidth, sampleHeight);
  let sum = 0;
  const pixels = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
  }
  return sum / pixels;
}

async function waitForDecodedFrame(video: HTMLVideoElement) {
  const videoWithFrameCallback = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (callback: () => void) => void;
  };
  if (typeof videoWithFrameCallback.requestVideoFrameCallback === 'function') {
    await new Promise<void>(resolve => {
      videoWithFrameCallback.requestVideoFrameCallback(() => resolve());
    });
    return;
  }

  if (video.readyState >= 2 && video.videoWidth > 0) return;

  await Promise.race([
    new Promise<void>(resolve => {
      video.addEventListener('loadeddata', () => resolve(), {once: true});
    }),
    wait(1500),
  ]);
}

export async function capturePhotoFromTrack(mediaStreamTrack: MediaStreamTrack): Promise<Blob | null> {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.setAttribute('playsinline', 'true');
  video.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
  const stream = new MediaStream([mediaStreamTrack]);

  try {
    video.srcObject = stream;
    document.body.appendChild(video);
    await video.play();
    await waitForDecodedFrame(video);
    await wait(600);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const context = canvas.getContext('2d');
    if (!context) return null;

    for (let attempt = 0; attempt < 6; attempt += 1) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      if (averageLuminance(context, canvas.width, canvas.height) >= 12 || attempt === 5) {
        return await new Promise(resolve => {
          canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.86);
        });
      }
      await wait(250);
    }
    return null;
  } catch {
    return null;
  } finally {
    video.remove();
  }
}

export function pickCallContextDevice(devices: IntercomDevice[], tenant: Tenant) {
  const apartmentId = tenant.apartment?.id;
  const buildingId = tenant.building?.id || tenant.room?.buildingId;
  const inProperty = devices.filter(device => {
    if (apartmentId) return device.apartmentId === apartmentId;
    return true;
  });
  if (!inProperty.length) return null;
  return (
    inProperty.find(device => buildingId && device.buildingId === buildingId) ||
    inProperty.find(device => device.deviceType === 'VIRTUAL') ||
    inProperty[0]
  );
}
